import { ref } from "vue";
import HttpClient from "project-blue-http-client";
import { GQL_QUERIES } from "@/utils/APIs";
import * as XLSX from 'xlsx';

export const marketData = ref([]);
export const supplyData = ref([]);
export const demandData = ref([]);
export const priceData = ref([]);
export const tradeData = ref([]);
export const countryData = ref([]);
export const reqSignal = ref(new AbortController());
export const inProgress = ref(false);
export const products = ref([]);
export const countries = ref([]);
export const priceMetadata = ref([]);

export const getMarketData = async (elementCode, startYear, endYear, product) => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    inProgress.value = true;

    let client = new HttpClient(gqlQuery.baseUrl);
    const data = {
        "elements": [elementCode],
        "dataSources": [],
        "countries": [],
        "products": product ? [product] : [],
        "types": [],
        "startDate": startYear,
        "endDate": endYear
    }
    const response = await client.post(`element/searchdatadashboardsearch`, data)
    inProgress.value = false
    marketData.value = response?.data || []
}

export const getSupplyData = async (elementCode, product, countries, startYear, endYear) => {
    supplyData.value = await getDashboardData(elementCode, product, countries, startYear, endYear, "supply");
}
export const getDemandData = async (elementCode, product, countries, startYear, endYear) => {
    demandData.value = await getAllDemandData(elementCode, product, countries, startYear, endYear);
}
export const getPriceData = async (elementCode, product, countries, startYear, endYear) => {
    priceData.value = await getDashboardData(elementCode, product, countries, startYear, endYear, "price");
}
export const getTradeData = async (elementCode, product, countries, startYear, endYear, type) => {
    tradeData.value = await getDashboardData(elementCode, product, countries, startYear, endYear, "trade", type);
}

export const getCountryDataSupply = async (elementCode, product, countries, startYear, endYear, type) => {
    countryData.value = await getDashboardData(elementCode, product, countries, startYear, endYear, "countrySupply", type);
}
export const getCountryDataTrade = async (elementCode, product, countries, startYear, endYear, type) => {
    countryData.value = await getDashboardData(elementCode, product, countries, startYear, endYear, "countryTrade", type);
}

/**
 * Fetch Sankey (import/export partner) data for the Trade Flow tab.
 * Calls SearchDataDashboardCountryTradeFlow — returns { country, hsCode, year, imports, exports }
 * where imports/exports are [{ partner, value, units }] arrays.
 *
 * @param {string} elementCode - commodity element code
 * @param {string} country     - reporting country code
 * @param {string} hsCode      - HS code (ProductForm) to filter by
 * @param {number} year        - single year for the Sankey
 */
/**
 * Fetch all countries with their Region field from the Assets API.
 * Returns a map of { countryName → region } for use in the Trade Flow region filter.
 */
export const getCountryRegionMap = async () => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    const client = new HttpClient(gqlQuery.baseUrl);
    const response = await client.get('country/getCountries');
    const list = response?.data || response || [];
    const map = {};
    list.forEach(c => { if (c.name && c.region) map[c.name] = c.region; });
    return map;
};

export const getCountryTradeFlowData = async (elementCode, country, hsCode, year) => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    const client = new HttpClient(gqlQuery.baseUrl);
    const payload = {
        element: elementCode,
        countries: [country],
        product: hsCode,
        startYear: year,
    };
    const response = await client.post('element/searchDataDashboardCountryTradeFlow', payload);
    return response || { imports: [], exports: [] };
};

/**
 * Fetch all available HS codes (ProductForm values) for a given commodity's Trade_flow data.
 * Used to populate the Import / Export HS Code dropdowns on the Trade Flow tab.
 *
 * @param {string} elementCode - commodity element code
 * @param {string} country     - reporting country code (optional, pass null for all)
 */
/**
 * Fetch all trade flow rows for a given commodity, optional HS code and year range.
 * Uses the same SearchDataDashboardTradeFlow endpoint as getTradeFlowHsCodes but
 * returns the full TradeFlowResult rows (Country, TradePartner, TypeName, YearlyData, etc.)
 * so callers can build chord diagrams or other aggregate views.
 *
 * @param {string} elementCode
 * @param {string|null} hsCode      - ProductForm / HS code (null = all)
 * @param {string|number} startYear
 * @param {string|number} endYear
 */
export const getGlobalTradeFlowData = async (elementCode, hsCode, startYear, endYear) => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    const client = new HttpClient(gqlQuery.baseUrl);
    const payload = {
        element: elementCode,
        product: hsCode || null,
        startDate: String(startYear),
        endDate:   String(endYear),
    };
    const response = await client.post('element/searchDataDashboardTradeFlow', payload);
    return response?.data || [];
};

// Trade flows are only ever charted from EXPORT rows (Country exports to
// TradePartner), so HS codes are only offered where such rows exist.
const fetchTradeFlowExportRows = async (elementCode, country) => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    const client = new HttpClient(gqlQuery.baseUrl);
    const payload = {
        element: elementCode,
        type: 'EXPORT',
        countries: country ? [country] : [],
        startDate: '2000',
        endDate: '2050',
    };
    const response = await client.post('element/searchDataDashboardTradeFlow', payload);
    return response?.data || [];
};

// A row's year range: its declared Start/End Year (upload columns N/O, the
// same range the other dashboards' date pickers use), falling back to the
// years it actually has non-zero values for when none was declared.
const rowYearRange = (r) => {
    const years = Object.entries(r.yearlyData || {}).filter(([, v]) => Number(v)).map(([y]) => Number(y));
    return {
        startYear: r.startYear ?? (years.length ? Math.min(...years) : null),
        endYear: r.endYear ?? (years.length ? Math.max(...years) : null),
    };
};

/**
 * The overall { startYear, endYear } span of a list of items that each carry
 * startYear/endYear (HS code options, products, countries), or null when
 * none of them has a range.
 */
export const tradeFlowYearSpan = (items) => {
    const starts = (items || []).map(x => x.startYear).filter(y => y != null);
    const ends = (items || []).map(x => x.endYear).filter(y => y != null);
    if (!starts.length || !ends.length) return null;
    return { startYear: Math.min(...starts), endYear: Math.max(...ends) };
};

// Unique { label, value, startYear, endYear } options from the rows'
// productForm (HS code) values, each with the year span of its rows
const hsCodeOptions = (rows) => {
    const byCode = new Map();
    rows.filter(r => r.productForm).forEach(r => {
        if (!byCode.has(r.productForm)) byCode.set(r.productForm, []);
        byCode.get(r.productForm).push(rowYearRange(r));
    });
    return [...byCode].map(([code, ranges]) => ({
        label: code,
        value: code,
        ...(tradeFlowYearSpan(ranges) ?? { startYear: null, endYear: null }),
    }));
};

export const getTradeFlowHsCodes = async (elementCode, country) =>
    hsCodeOptions(await fetchTradeFlowExportRows(elementCode, country));

/**
 * HS codes for one country, split by the direction that actually has data:
 * `imports` - codes where another country exports TO it (it's the TradePartner),
 * `exports` - codes where it exports to others (it's the reporting Country).
 */
export const getTradeFlowHsCodesBySide = async (elementCode, country) => {
    const rows = await fetchTradeFlowExportRows(elementCode, country);
    return {
        imports: hsCodeOptions(rows.filter(r => r.tradePartner === country)),
        exports: hsCodeOptions(rows.filter(r => r.country === country)),
    };
};

export const getCountryData = async (elementCode, product, countries, startYear, endYear, type) => {
    countryData.value = await getDashboardData(elementCode, product, countries, startYear, endYear, "country", type);
}

export const getDashboardData = async (elementCode, product, countries, startYear, endYear, type, tradeType) => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    inProgress.value = true;

    let client = new HttpClient(gqlQuery.baseUrl);
    const data = {
        "element": elementCode,
        "product": product,
        "startDate": startYear,
        "countries": countries,
        "endDate": endYear,
        "type": tradeType
    }
    const response = await client.post(`element/searchDataDashboard${type}`, data)
    inProgress.value = false
    return response?.data || []
}
export const getAllDemandData = async (elementCode, product, countries, startYear, endYear) => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    inProgress.value = true;

    let client = new HttpClient(gqlQuery.baseUrl);
    const data = {
        "element": elementCode,
        "product": product,
        "startDate": startYear,
        "countries": countries,
        "endDate": endYear
    }
    const response = await client.post(`element/searchDataDashboardDemand`, data)
    console.log("response ==>", response)
    inProgress.value = false
    return response || []
}

export const getCommodityProductsAndCountries = async (elementCode, type) => {
    const gqlQuery = GQL_QUERIES.GET_SUBSCRIBED_ASSET_PROFILE;
    inProgress.value = true;

    // Clear immediately so the product/country dropdowns don't show stale options
    // from the previous commodity while the new list is being fetched.
    products.value = [];
    countries.value = [];

    let client = new HttpClient(gqlQuery.baseUrl);
    // The API binds `type` to its MarketDataType enum, where Trade Flow is
    // `Trade_Flow` - "Trade Flow" (the tab name) fails to bind and silently
    // falls back to the enum's first value (Price), returning Price countries.
    const apiType = String(type).replace(/ /g, '_');
    const response = await client.get(`element/getCommodityProductsAndCountries/${elementCode}/${apiType}`);
    inProgress.value = false
    products.value = response?.products || []
    countries.value = response?.countries || []
}

export const exportToCSV = (rows, minYear, maxYear, fileName, type) => {
    let staticHeaders = {};
    switch (type) {
        case "supply":
            staticHeaders = {
                country: "Country",
                units: "Units",
                productForm: "Product Form",
            };
            break;
        case "demand-application":
            staticHeaders = {
                applicationDemand: "Application Demand",
                units: "Units",
            };
            break;
        case "demand-region":
            staticHeaders = {
                country: "Country",
                units: "Units",
            };
            break;
        case "price":
            staticHeaders = {
                units: "Units",
                productForm: "Product Form",
            };
            break;
        case "trade":
            staticHeaders = {
                country: "Country",
                productForm: "Product Form",
                units: "Units",
            };
            break;

        default:
            staticHeaders = {
                dataType: "Data Type",
                units: "Units",
                productForm: "Product Form",
            };
            break;
    }

    // Generate dynamic headers for each year
    const yearHeaders = [];
    for (let y = minYear; y <= maxYear; y++) {
        yearHeaders.push(y.toString());
    }

    // Build full CSV header row
    const csvHeader = [
        ...Object.values(staticHeaders),
        ...yearHeaders
    ].join(",") + "\n";

    // Flatten out any nested child rows (e.g. trade's country-level breakdown)
    // so they are included in the export rather than silently dropped.
    const flatRows = rows.flatMap(row => Array.isArray(row.children) && row.children.length
        ? [row, ...row.children.map(child => ({ ...child, country: child.country || row.country }))]
        : [row]);

    // Build each data row
    const csvRows = flatRows.map(row => {
        const staticValues = Object.keys(staticHeaders).map(key =>
            `"${row[key] ?? ""}"`
        );

        // Add year values (assuming row.yearlyData is a dictionary)
        const yearValues = yearHeaders.map(y =>
            `"${row[y] ?? ""}"`
        );

        return [...staticValues, ...yearValues].join(",");
    }).join("\n");

    const csv = csvHeader + csvRows;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", fileName);
    link.click();
}

export const exportToExcel = (rows, minYear, maxYear, fileName, type) => {
    let staticHeaders = {};
    switch (type) {
        case "supply":
            staticHeaders = {
                country: "Country",
                units: "Units",
                productForm: "Product Form",
            };
            break;
        case "demand-application":
            staticHeaders = {
                applicationDemand: "Application Demand",
                units: "Units",
            };
            break;
        case "demand-region":
            staticHeaders = {
                country: "Country",
                units: "Units",
            };
            break;
        case "price":
            staticHeaders = {
                units: "Units",
                productForm: "Product Form",
            };
            break;
        case "trade":
            staticHeaders = {
                country: "Country",
                productForm: "Product Form",
                units: "Units",
            };
            break;
        default:
            staticHeaders = {
                dataType: "Data Type",
                units: "Units",
                productForm: "Product Form",
            };
            break;
    }

    // Dynamic headers (years)
    const yearHeaders = [];
    for (let y = minYear; y <= maxYear; y++) {
        yearHeaders.push(y.toString());
    }

    // Final header row
    const headerRow = [...Object.values(staticHeaders), ...yearHeaders];

    // Flatten out any nested child rows (e.g. trade's country-level breakdown)
    // so they are included in the export rather than silently dropped.
    const flatRows = rows.flatMap(row => Array.isArray(row.children) && row.children.length
        ? [row, ...row.children.map(child => ({ ...child, country: child.country || row.country }))]
        : [row]);

    // Build Excel rows
    const excelRows = flatRows.map(row => {
        const staticValues = Object.keys(staticHeaders).map(key => row[key] ?? "");
        const yearValues = yearHeaders.map(y => row[y] ?? "");
        return [...staticValues, ...yearValues];
    });

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...excelRows]);

    // Create workbook & append sheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // Generate Excel file
    XLSX.writeFile(workbook, fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`);
};

export const getPriceMetadata = async (
    elementCode,
    startYear = 2015,
    endYear = 2035
) => {

    priceMetadata.value = await getDashboardData(
        elementCode,
        null,
        null,
        startYear,
        endYear,
        "price"
    );

}